<script setup lang="ts">
import {ref, watchEffect} from 'vue'
import {type AuditLogEntry} from '@/backend/audit'
import useBackend from '@/composition/useBackend'

const props = defineProps<{
  row: any
}>()

const { audit } = useBackend()

const selectedEntry = ref<AuditLogEntry | undefined>(undefined)
const isLoading = ref(false)
const error = ref<string | undefined>(undefined)

// Watch for row changes and fetch entry
watchEffect(async () => {
  if (!props.row || !props.row.key) {
    selectedEntry.value = undefined
    error.value = undefined
    return
  }

  isLoading.value = true
  error.value = undefined

  try {
    selectedEntry.value = await audit.getById(props.row.key)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load audit entry'
    selectedEntry.value = undefined
  } finally {
    isLoading.value = false
  }
})

function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '(null)'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleString()
}
</script>

<template>
  <div v-if="isLoading" class="loading">
    <p>Loading...</p>
  </div>

  <div v-else-if="error" class="error">
    <p>{{ error }}</p>
  </div>

  <div v-else-if="selectedEntry" class="audit-detail p2">
    <div>
      <div class="box-content-label">Field Changes</div>
      <dl class="box-content">
        <dt>Timestamp</dt>
        <dd>{{ formatTimestamp(selectedEntry.timestamp) }}</dd>
        <dt>Action</dt>
        <dd>
          {{ selectedEntry.action }}
        </dd>
        <dt>Resource</dt>
        <dd><code>{{ selectedEntry.resourcePath.join('/') }}</code></dd>
        <dt>Identity</dt>
        <dd><code>{{ selectedEntry.identityId }}</code></dd>
      </dl>
    </div>
    
    <div>
      <div class="box-content-label">Field Changes</div>
      <div class="box-content p">
        <table class="changes-table" v-if="selectedEntry.changes.length > 0">
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in selectedEntry.changes" :key="change.field">
              <td class="field-cell"><code>{{ change.field }}</code></td>
              <td class="values-cell">
                <div class="changes-container">
                  <div class="values-diff">
                    <div class="old-value p"><pre>{{ formatValue(change.oldValue) }}</pre></div>
                    <div class="new-value p"><pre>{{ formatValue(change.newValue) }}</pre></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-changes">No field changes recorded</p>
      </div>
    </div>
  </div>

  <div v-else class="empty-selection">
    <p>Select an audit entry to view details</p>
  </div>
</template>

<style scoped>
.audit-detail {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.audit-detail h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 500;
}

.changes-table {
  width: 100%;
  border-collapse: collapse;
}

.changes-table th,
.changes-table td {
  border: 1px solid var(--color-border);
  text-align: left;
}

.changes-table td {
  vertical-align: top;
}

.changes-table pre {
  margin: 0;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.changes-table code {
  font-family: monospace;
  background: var(--color-background-secondary);
  padding: 0;
}

.no-changes {
  color: var(--color-text-secondary);
  font-style: italic;
}

.empty-selection,
.loading,
.error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-secondary);
  font-size: 16px;
}

.error {
  color: var(--color-danger-text);
}

.field-cell {
  white-space: nowrap;
}

.values-cell {
  width: 100%;
}

.changes-container {
  container-type: inline-size;
}

.values-diff {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

@container (max-width: 600px) {
  .values-diff {
    display: flex;
    flex-direction: column;
  } 
}

.old-value {
  background-color: var(--color-error-100);
  border-radius: 4px 4px 0 0;
}

.new-value {
  background-color: var(--color-success-100);
  border-radius: 0 0 4px 4px;
}
</style>
